/**
 * IdP Service - Identity Provider client
 * Handles authentication with Adder IdP
 */

// Base explícita (build/dev). En producción el build no la lleva, así que el
// valor de verdad se pide al backend: `IDP_URL` es el IdP de referencia de
// esta instancia y es el mismo al que manda el login.
const ENV_IDP = (process.env.REACT_APP_IDP_URL || window.__ENV__?.IDP_URL || '').replace(/\/$/, '');
// Empty string → relative URLs (/api/…) proxied by nginx.
const API_BASE = (process.env.REACT_APP_API_URL ?? window.__ENV__?.API_URL ?? '').replace(/\/$/, '');

/** Base del IdP resuelta en caliente desde `/api/version` (se cachea). */
let runtimeIdpBase = null;
let idpBasePromise = null;

/** Último recurso: mismo host, puerto de desarrollo del IdP. */
function derivedIdpBase() {
  try {
    return `${window.location.protocol}//${window.location.hostname}:5001`;
  } catch (e) {
    return 'http://localhost:5001';
  }
}

class IdPService {
  /**
   * Get current user from token
   */
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!userStr || !token) return null;
    
    try {
      return JSON.parse(userStr);
    } catch (e) {
      return null;
    }
  }

  /**
   * Get authentication token
   */
  getToken() {
    return localStorage.getItem('token');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!this.getToken() && !!this.getCurrentUser();
  }

  /**
   * Canjear el código de un solo uso que devuelve el IdP al volver del SSO.
   *
   * Por qué existe: el SSO ya no devuelve el token en la URL
   * (`?idp_token=eyJ...`), que acababa en el historial del navegador, en el
   * `Referer` y en los logs del proxy. Devuelve un código de 60 segundos y un
   * solo uso, y el backend lo canjea contra el IdP con la credencial de la
   * aplicación. El token no pasa por la barra de direcciones.
   */
  async exchangeCode(code) {
    const response = await fetch(`${API_BASE}/api/auth/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      throw new Error(`El canje del código fue rechazado (${response.status})`);
    }

    const datos = await response.json();
    if (!datos.token) {
      throw new Error('El canje no devolvió token');
    }
    return datos.token;
  }

  /**
   * Verify token with IdP
   */
  async verifyToken() {
    const token = this.getToken();
    if (!token) return false;

    try {
      const base = await this.resolveBaseUrl();
      const response = await fetch(`${base}/api/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.ok;
    } catch (error) {
      console.error('Token verification failed:', error);
      return false;
    }
  }

  /**
   * Logout user
   */
  async logout() {
    const token = this.getToken();
    
    try {
      // Notify IdP about logout (optional)
      if (token) {
        const base = await this.resolveBaseUrl();
        await fetch(`${base}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).catch(() => {
          // Ignore errors, just local logout
        });
      }
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  }

  /**
   * Get user info from IdP
   */
  async getUserInfo() {
    const token = this.getToken();
    if (!token) return null;

    try {
      const base = await this.resolveBaseUrl();
      const response = await fetch(`${base}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.user || null;
      }
    } catch (error) {
      console.error('Failed to get user info:', error);
    }

    return null;
  }

  /**
   * Get IdP login URL
   */
  getLoginUrl(returnUrl) {
    const target = returnUrl || window.location.origin;
    return `${API_BASE}/api/auth/login?return_to=${encodeURIComponent(target)}`;
  }

  /**
   * Get IdP logout URL
   */
  getLogoutUrl() {
    return `${this.getBaseUrl()}/logout`;
  }

  /**
   * Base del IdP para el navegador.
   *
   * Orden de preferencia:
   *   1. `REACT_APP_IDP_URL` / `window.__ENV__` (build o entorno de desarrollo).
   *   2. La que haya resuelto `resolveBaseUrl()` preguntando al backend.
   *   3. Mismo host en el puerto 5001 (IdP local).
   */
  getBaseUrl() {
    return ENV_IDP || runtimeIdpBase || derivedIdpBase();
  }

  /**
   * Resuelve la base del IdP preguntando al backend (`/api/version` → `idp_url`).
   *
   * Es lo que hace funcionar el enlace de «Mi perfil» en producción: la URL
   * sale del IdP configurado en la instancia, no de un `localhost` embebido en
   * el build. Se hace una sola vez y se cachea (incluso si falla).
   */
  async resolveBaseUrl() {
    if (ENV_IDP) return ENV_IDP;
    if (runtimeIdpBase) return runtimeIdpBase;
    if (!idpBasePromise) {
      idpBasePromise = fetch(`${API_BASE}/api/version`)
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
          const url = String(data?.idp_url || '').trim().replace(/\/$/, '');
          if (url) runtimeIdpBase = url;
          return url || null;
        })
        .catch(() => null);
    }
    await idpBasePromise;
    return this.getBaseUrl();
  }

  /** URL de la pantalla de perfil del IdP (identidad, contraseña, 2FA). */
  getProfileUrl() {
    return `${this.getBaseUrl()}/profile/settings`;
  }

  async resolveProfileUrl() {
    const base = await this.resolveBaseUrl();
    return `${base}/profile/settings`;
  }
}

export default new IdPService();
